import type { Item } from '@backend/prisma';
import type {
  CreateItemShareInvitationRequest,
  GroupInvitationResponse,
  ItemShareInvitationResponse,
  PendingInvitationResponse,
  PendingInvitationsCountResponse,
  ShareWithPermissions,
} from '@stw/types';

import { stwFetch } from '@/utils/stwFetch';

/**
 * Create an item share invitation
 */
export const createItemShareInvitation = async (
  itemId: string,
  data: CreateItemShareInvitationRequest
): Promise<{ id: string }> => {
  return await stwFetch('POST', `/invitation/item/${itemId}`, data);
};

/**
 * Create a group invitation
 */
export const createGroupInvitation = async (
  groupId: string,
  data: { invitee_id: string }
): Promise<{ id: string }> => {
  return await stwFetch('POST', `/invitation/group/${groupId}`, data);
};

/**
 * Get all pending invitations for the current user
 */
export const getPendingInvitations = async (): Promise<
  PendingInvitationResponse[]
> => {
  return await stwFetch('GET', '/invitation/pending');
};

/**
 * Get count of pending invitations for the current user
 */
export const getPendingInvitationCount =
  async (): Promise<PendingInvitationsCountResponse> => {
    return await stwFetch('GET', '/invitation/pending/count');
  };

/**
 * Get invitation details by token (public endpoint)
 */
export const getInvitationByToken = async (
  type: 'item' | 'group',
  token: string
): Promise<ItemShareInvitationResponse | GroupInvitationResponse> => {
  return await stwFetch('GET', `/invitation/${type}/${token}`);
};

/**
 * Accept an item share invitation
 */
export const acceptItemShareInvitation = async (
  token: string
): Promise<{ itemId: string; itemName: string }> => {
  return await stwFetch('POST', `/invitation/item/${token}/accept`);
};

/**
 * Decline an item share invitation
 */
export const declineItemShareInvitation = async (
  token: string
): Promise<void> => {
  await stwFetch('POST', `/invitation/item/${token}/decline`);
};

/**
 * Accept a group invitation
 */
export const acceptGroupInvitation = async (
  token: string
): Promise<{ groupId: string; groupName: string }> => {
  return await stwFetch('POST', `/invitation/group/${token}/accept`);
};

/**
 * Decline a group invitation
 */
export const declineGroupInvitation = async (token: string): Promise<void> => {
  await stwFetch('POST', `/invitation/group/${token}/decline`);
};

/**
 * Share an item to multiple users by email addresses (creates invitations)
 * @param itemId - The ID of the item to share
 * @param emails - Array of email addresses to share with
 * @param permissions - Optional permissions to set on shared copies
 * @returns Object with shared items and any invalid emails
 */
export const shareItemToUsers = async (
  itemId: Item['id'],
  emails: string[],
  permissions: ShareWithPermissions
): Promise<{
  shared_items: Array<{ item_id: string; email: string }>;
  invalid_emails: string[];
}> => {
  const response = await stwFetch('POST', `/invitation/item/${itemId}/share`, {
    emails,
    permissions,
  });
  return response;
};

/**
 * Share an item to all members of a group (creates invitations)
 * @param itemId - The ID of the item to share
 * @param groupId - The ID of the group to share with
 * @param permissions - Optional permissions to set on shared copies
 */
export const shareItemToGroup = async (
  itemId: string,
  groupId: string,
  permissions: ShareWithPermissions
): Promise<{
  shared_count: number;
  shared_items: Array<{ item_id: string; user_id: string }>;
}> => {
  const response = await stwFetch(
    'POST',
    `/invitation/item/${itemId}/share-to-group`,
    {
      group_id: groupId,
      permissions,
    }
  );
  return response;
};
